/**
 * Kestner at the desk: the writing loop, and the one gesture that is not work.
 *
 * `docs/beat-11-plan.md`, *Kestner*: posture, light, and **one gesture that is
 * not work** — *"He takes his glasses off and presses the bridge of his nose.
 * It costs one pose and it is the whole characterisation."* The look-up is the
 * same event: he straightens, looks at nothing for about a second, and goes
 * back to the page.
 *
 * **Nothing reads any of this, and that is the design, not an omission.** There
 * is no detection state in beat 11 and no value in the beat changes when he
 * looks up; the plan commits to it in as many words so that no later session
 * mistakes the look-up for a mechanic and hangs a cone off it. It exists because
 * the player will freeze, and the player freezing is the only tension this beat
 * gets. `kestnerDeskPose` is a pure function of presentation time and nothing
 * in the simulation calls it — `tests/e2e/kestner-desk.spec.ts` holds that.
 *
 * Split the way the rest of `src/anim` is: a pure pose from a clock, and a rig
 * that is the only thing that touches the `Object3D`s.
 */
import * as THREE from 'three';
import { ease } from '@/anim/types';
import { GLASSES_ON_FACE, reachSeatedArm, type KestnerSeatedParts } from '@/models/env/kestnerSeated';

/**
 * When the gesture happens, in seconds of the room's presentation clock.
 *
 * Scheduled, once, and early enough to land while a player is still crossing:
 * the crossing is ~15 s for somebody who walks it, and the fade opens on the
 * first second. Nine seconds puts it in the middle of the room for a player
 * who has started moving and in plain view for one who has frozen in the
 * corner, which is the player the plan says it is for.
 */
export const GESTURE_AT = 9;

/** The gesture's own beats, in seconds from `GESTURE_AT`. */
const G = {
  /** Sits up and brings the hand to his face. */
  riseEnd: 1.1,
  /** Glasses come off as the hand arrives. */
  off: 1.15,
  /** Holding the bridge of his nose, head bowed into it. */
  pressEnd: 2.4,
  /** Hand drops, head comes up. */
  lookFrom: 3.0,
  /** About a second of looking at nothing. */
  lookTo: 4.1,
  /** Hand back up; glasses back on at `on`. */
  on: 4.6,
  /** Folded back over the page. */
  settled: 5.8,
} as const;
export const GESTURE_LENGTH = G.settled;

export interface KestnerDeskPose {
  /** Delta on the waist fold. Negative sits him up. */
  readonly waist: number;
  /** Delta on the head's pitch. Negative lifts it. */
  readonly neck: number;
  /** The writing stroke: right shoulder yaw delta. */
  readonly stroke: number;
  /** Down the column: right shoulder pitch delta, one step per written figure. */
  readonly line: number;
  /** 0 = left forearm on the page, 1 = left hand at the bridge of his nose. */
  readonly faceReach: number;
  readonly glassesOff: boolean;
}

function span(t: number, from: number, to: number): number {
  return ease.inOut(Math.min(1, Math.max(0, (t - from) / (to - from))));
}

/**
 * Writing: a figure is 1.4 s of small strokes travelling along the line, then
 * 0.6 s with the pencil lifted, then the next line down. Six lines and back to
 * the top — a column, which is what he keeps.
 */
const FIGURE = 1.4;
const GAP = 0.6;
const LINES = 6;

function writing(t: number): { stroke: number; line: number } {
  const cycle = FIGURE + GAP;
  const index = Math.floor(t / cycle);
  const within = t - index * cycle;
  const lineStep = index % LINES;
  if (within < FIGURE) {
    const u = within / FIGURE;
    // Strokes inside an envelope, riding a slow drift along the line. The
    // amplitude is a few centimetres at the hand: a pencil, not a paintbrush.
    const strokes = 0.028 * Math.sin(u * Math.PI * 2 * 4.5) * Math.sin(u * Math.PI);
    return { stroke: strokes + 0.05 * u, line: lineStep * 0.02 };
  }
  // Pencil lifted, carried back to the start of the next line.
  const back = ease.inOut((within - FIGURE) / GAP);
  const nextLine = (index + 1) % LINES;
  return { stroke: 0.05 * (1 - back), line: (lineStep + (nextLine - lineStep) * back) * 0.02 };
}

export function kestnerDeskPose(t: number): KestnerDeskPose {
  const breath = 0.008 * Math.sin((t / 4.2) * Math.PI * 2);
  const g = t - GESTURE_AT;
  const write = writing(t);
  if (g < 0 || g >= G.settled) {
    return { waist: breath, neck: write.line * 0.6, stroke: write.stroke, line: write.line, faceReach: 0, glassesOff: false };
  }
  // Up, then back down; the two halves are the same curve run each way.
  const sit = span(g, 0, G.riseEnd) * (1 - span(g, G.on, G.settled));
  const toFace = span(g, 0.2, G.riseEnd) * (1 - span(g, G.pressEnd, G.lookFrom))
    + span(g, G.lookTo, G.on - 0.05) * (1 - span(g, G.on + 0.1, G.settled));
  // Bowed into the hand, then up at nothing, then back to the page.
  const bow = span(g, G.riseEnd, G.riseEnd + 0.5) * (1 - span(g, G.pressEnd, G.lookFrom));
  const look = span(g, G.pressEnd, G.lookFrom) * (1 - span(g, G.lookTo, G.on));
  // The writing stops for the whole gesture and eases back in after it.
  const writingWeight = 1 - span(g, 0, 0.4) * (1 - span(g, G.on + 0.2, G.settled));
  return {
    waist: breath - 0.24 * sit,
    neck: -0.08 * sit + 0.14 * bow - 0.5 * look + write.line * 0.6 * writingWeight,
    stroke: write.stroke * writingWeight,
    line: write.line * writingWeight,
    // Half-way down while he looks, the folded glasses still in it.
    faceReach: Math.max(toFace, 0.35 * look),
    glassesOff: g >= G.off && g < G.on,
  };
}

/** Where the held glasses sit in the left hand, in the elbow's frame. */
const GLASSES_IN_HAND = { x: 0, y: -0.31, z: 0.035 } as const;
/** In front of the bridge, in the neck's frame: where the hand centre goes. */
const BRIDGE_REACH = { x: 0, y: GLASSES_ON_FACE.y - 0.045, z: GLASSES_ON_FACE.z + 0.05 } as const;

/**
 * Applies a `KestnerDeskPose` to the seated figure. Bind rotations are read once
 * at construction and every pose is added to them, so the asset's solved rest
 * pose (hands on the page) is what zero means.
 */
export class KestnerDeskRig {
  readonly #parts: KestnerSeatedParts;
  readonly #bind: { waist: number; neck: number; right: THREE.Euler; left: THREE.Euler; elbow: number };
  #glassesOff = false;
  readonly #target = new THREE.Vector3();

  constructor(parts: KestnerSeatedParts) {
    this.#parts = parts;
    this.#bind = {
      waist: parts.waist.rotation.x,
      neck: parts.neck.rotation.x,
      right: parts.shoulderRight.rotation.clone(),
      left: parts.shoulderLeft.rotation.clone(),
      elbow: parts.elbowLeft.rotation.x,
    };
  }

  apply(pose: KestnerDeskPose): void {
    const p = this.#parts;
    const b = this.#bind;
    p.waist.rotation.x = b.waist + pose.waist;
    p.neck.rotation.x = b.neck + pose.neck;
    p.shoulderRight.rotation.set(b.right.x + pose.line, b.right.y + pose.stroke, b.right.z);

    // The hand goes to the face wherever the face is this frame: the target is
    // taken from the neck's live pitch, so sitting up and bowing into the hand
    // cannot leave the fingers a few centimetres off his nose.
    const w = pose.faceReach;
    if (w > 0) {
      const c = Math.cos(p.neck.rotation.x);
      const s = Math.sin(p.neck.rotation.x);
      this.#target.set(
        p.neck.position.x + BRIDGE_REACH.x,
        p.neck.position.y + BRIDGE_REACH.y * c - BRIDGE_REACH.z * s,
        p.neck.position.z + BRIDGE_REACH.y * s + BRIDGE_REACH.z * c,
      );
      const solved = reachSeatedArm(p.shoulderLeft.position, this.#target);
      p.shoulderLeft.rotation.set(
        b.left.x + (solved.shoulderX - b.left.x) * w,
        b.left.y,
        b.left.z + (solved.shoulderZ - b.left.z) * w,
      );
      p.elbowLeft.rotation.x = b.elbow + (solved.elbowX - b.elbow) * w;
    } else {
      p.shoulderLeft.rotation.copy(b.left);
      p.elbowLeft.rotation.x = b.elbow;
    }

    if (pose.glassesOff !== this.#glassesOff) {
      this.#glassesOff = pose.glassesOff;
      const glasses = p.glasses;
      if (pose.glassesOff) {
        p.elbowLeft.add(glasses);
        glasses.position.set(GLASSES_IN_HAND.x, GLASSES_IN_HAND.y, GLASSES_IN_HAND.z);
        // Folded shut and held edge-on in the fingers: the lenses turn to face
        // along the forearm, so what the lamp catches is a sliver, not a pane.
        glasses.rotation.set(0, Math.PI / 2, 0);
        glasses.scale.set(1, 1, 0.3);
      } else {
        p.neck.add(glasses);
        glasses.position.set(GLASSES_ON_FACE.x, GLASSES_ON_FACE.y, GLASSES_ON_FACE.z);
        glasses.rotation.set(0, 0, 0);
        glasses.scale.set(1, 1, 1);
      }
    }
  }

  get glassesOff(): boolean {
    return this.#glassesOff;
  }
}
